/**
 * One code field and a submit button, for an authenticator code or a backup code. The Worker's
 * refusal codes become sentences here. After five wrong codes the Worker ends the session, so that
 * refusal says to sign in again rather than to retry. A page given `onEnded` replaces the form
 * instead, because every later code would be refused as signed out.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState } from "react";
import { Button } from "@lattice/ui/Button";
import { authStyles } from "../AuthLayout.tsx";
const MESSAGES = new Map([["wrong_code", "That code didn't match. Codes change every 30 seconds, so use the newest one."], ["too_many_wrong_codes", "Too many wrong codes. Sign in again with your email in a few minutes."], ["nothing_to_confirm", "This setup has expired. Refresh the page to start again."], ["not_enrolled", "No authenticator is set up yet. Refresh the page to set one up."]]);
export type EndedOutcome = "too_many_wrong_codes" | "signed_out";
function isEnded(outcome: string): outcome is EndedOutcome {
  return outcome === "too_many_wrong_codes" || outcome === "signed_out";
}
export function CodeForm({
  submitLabel,
  check,
  onPassed,
  acceptsBackupCodes = false,
  onEnded,
  disabledReason,
  focusOnMount = false
}: {
  readonly submitLabel: string;
  /** Sends the code: "ok", or the Worker's refusal code. */
  readonly check: (code: string) => Promise<string>;
  readonly onPassed: () => void;
  readonly acceptsBackupCodes?: boolean;
  /** Called instead of showing a refusal when the session has ended or may take no more codes. */
  readonly onEnded?: (outcome: EndedOutcome) => void;
  /** Holds the submit button back and says why, such as backup codes not yet saved. */
  readonly disabledReason?: string;
  /** Puts the cursor in the field when the screen opens, for a page whose only job is this code. */
  readonly focusOnMount?: boolean;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /* Authenticator codes are six digits, so a phone shows its number pad until a backup code is wanted. */
  const [letters, setLetters] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focusOnMount) field.current?.focus();
  }, [focusOnMount]);
  const submit = async () => {
    setBusy(true);
    setProblem(null);
    const outcome = await check(code.replace(/[\s-]/g, "")).catch(() => "failed");

    /* Left busy once it passes, so a second press cannot send the same code while the page moves on. */
    if (outcome === "ok") {
      onPassed();
      return;
    }
    setBusy(false);
    if (onEnded !== undefined && isEnded(outcome)) onEnded(outcome);else setProblem(MESSAGES.get(outcome) ?? "We couldn't check that code. Try again.");
  };
  return <form {...stylex.props(authStyles.form)} onSubmit={event => {
    event.preventDefault();
    if (!busy) void submit();
  }}>
      <div {...stylex.props(authStyles.field)}>
        <label htmlFor="second-factor-code" {...stylex.props(authStyles.label)}>
          {letters ? "Backup code" : "Six-digit code"}
        </label>
        <input ref={field} id="second-factor-code" data-testid="second-factor-code" inputMode={letters ? "text" : "numeric"} autoComplete="one-time-code" aria-invalid={problem === null ? undefined : true} aria-describedby={problem === null ? undefined : "second-factor-problem"} value={code} onChange={event => setCode(event.target.value)} {...stylex.props(authStyles.input, authStyles.code)} />
        {acceptsBackupCodes ? <Button tone="quiet" size="md" testId="second-factor-letters" onClick={() => {
        setLetters(now => !now);
        setProblem(null);
        /* A phone changes its keyboard only for a field that was left and entered again. */
        field.current?.blur();
        requestAnimationFrame(() => field.current?.focus());
      }}>
            {letters ? "Use a code from my app instead" : "Type a backup code instead"}
          </Button> : null}
        {problem === null ? null : <p id="second-factor-problem" role="alert" {...stylex.props(authStyles.error)}>
            {problem}
          </p>}
      </div>
      <Button type="submit" busy={busy} disabled={disabledReason !== undefined} disabledReason={disabledReason} testId="second-factor-submit">
        {submitLabel}
      </Button>
      {disabledReason === undefined ? null : <p {...stylex.props(authStyles.body)} data-testid="second-factor-reason">
          {disabledReason}.
        </p>}
    </form>;
}